import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
    FaCheckCircle, FaTimesCircle, FaSpinner,
    FaMapMarkerAlt, FaCalendarAlt, FaClock,
    FaRupeeSign, FaCopy, FaHome, FaReceipt
} from "react-icons/fa";
import api from "../api/Api";

export default function BookingSuccess() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [booking, setBooking] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);

    const txnid = searchParams.get("txnid");
    const status = searchParams.get("status");

    useEffect(() => {

        if (!txnid) {
            navigate("/dashboard");
            return;
        }

        const fetchBooking = async () => {
            try {
                const token = localStorage.getItem("customerToken");
                const res = await api.post(`/bookingRoutes/booking-by-txn`, { txnid }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setBooking(res.data);
            } catch (e) {
                console.error("Failed to fetch booking:", e);
            } finally {
                setLoading(false);
            }
        };

        fetchBooking();
    }, [txnid, navigate]);

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const isSuccess = status === "success";

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light">
                <FaSpinner className="text-primary fa-spin" size={32} />
            </div>
        );
    }

    return (
        <div className="d-flex flex-column min-vh-100 bg-light">
            <div className="container-fluid p-3" style={{ maxWidth: "500px", margin: "0 auto" }}>

                {/* STATUS CARD */}
                <div className={`card border-0 shadow-sm rounded-4 mb-3 text-center p-4 ${isSuccess ? "bg-success bg-opacity-10" : "bg-danger bg-opacity-10"}`}>
                    {isSuccess
                        ? <FaCheckCircle size={60} className="text-success mb-3 mx-auto" />
                        : <FaTimesCircle size={60} className="text-danger mb-3 mx-auto" />
                    }
                    <h4 className={`fw-bold ${isSuccess ? "text-success" : "text-danger"}`}>
                        {isSuccess ? "Booking Confirmed!" : "Payment Failed"}
                    </h4>
                    <p className="text-muted small mb-0">
                        {isSuccess
                            ? "Your ride has been booked successfully."
                            : "Your payment was not completed. Please try again."}
                    </p>
                </div>

                {/* BOOKING DETAILS */}
                {isSuccess && booking && (
                    <div className="card border-0 shadow-sm rounded-4 mb-3">
                        <div className="card-body p-3">
                            <h6 className="fw-bold text-dark mb-3 small text-uppercase">Booking Details</h6>

                            {/* Reference No */}
                            <div className="d-flex justify-content-between align-items-center bg-light rounded-3 p-3 mb-3 border">
                                <div>
                                    <div className="text-muted" style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.5px" }}>BOOKING REF</div>
                                    <div className="fw-bold text-dark fs-5">{booking.confirmation_id}</div>
                                </div>
                                <button
                                    className="btn btn-sm btn-outline-primary rounded-pill px-3"
                                    onClick={() => handleCopy(booking.confirmation_id)}
                                >
                                    <FaCopy className="me-1" /> {copied ? "Copied!" : "Copy"}
                                </button>
                            </div>

                            <div className="d-flex justify-content-between small text-muted mb-2 border-bottom pb-2">
                                <span className="fw-semibold">Passenger</span>
                                <span className="fw-bold text-dark">{booking.gustcust_name}</span>
                            </div>
                            <div className="d-flex justify-content-between small text-muted mb-2 border-bottom pb-2">
                                <span className="fw-semibold d-flex align-items-center gap-1"><FaMapMarkerAlt className="text-success" /> Pickup</span>
                                <span className="fw-bold text-dark text-end" style={{ maxWidth: "200px" }}>{booking.pickup_city || "N/A"}</span>
                            </div>
                            <div className="d-flex justify-content-between small text-muted mb-2 border-bottom pb-2">
                                <span className="fw-semibold d-flex align-items-center gap-1"><FaCalendarAlt className="text-primary" /> Date</span>
                                <span className="fw-bold text-dark">
                                    {booking.arrive_dt ? new Date(booking.arrive_dt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "N/A"}
                                </span>
                            </div>
                            <div className="d-flex justify-content-between small text-muted mb-2 border-bottom pb-2">
                                <span className="fw-semibold d-flex align-items-center gap-1"><FaReceipt className="text-warning" /> Amount Paid</span>
                                <span className="fw-bold text-success">₹{Number(booking.payment_amnt).toLocaleString()}</span>
                            </div>
                            <div className="d-flex justify-content-between small text-muted">
                                <span className="fw-semibold">Transaction ID</span>
                                <span className="fw-bold text-dark" style={{ fontSize: "0.75rem" }}>{txnid}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* ACTIONS */}
                <div className="d-flex flex-column gap-2">
                    <button
                        className="btn w-100 py-3 fw-bold text-white rounded-pill shadow"
                        style={{ background: "linear-gradient(135deg, #ff7a00, #ff9500)" }}
                        onClick={() => navigate("/dashboard")}
                    >
                        <FaHome className="me-2" /> Go to Dashboard
                    </button>
                    {!isSuccess && (
                        <button
                            className="btn w-100 py-3 fw-bold rounded-pill border"
                            onClick={() => navigate(-1)}
                        >
                            Try Again
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}